/**
 * @format
 */
import { Injectable } from "@angular/core";
import { EditorService } from "./editor.service";
import { BaseToolService } from "./base-tool.service";
import { ToolAbstractService } from "./tool-abstract.service";
import { FontStyleArgument, SelectedElement, StyleProperty, StylingStatus } from "../types/editor-types";
import { ToolExtraArguments } from "../types/tool-extra-arguments";

@Injectable()
export class FontToolService extends BaseToolService implements ToolAbstractService {
   constructor(private editorService: EditorService) {
      super();
   }

   private _style: FontStyleArgument;
   private _START_ID = "START-STYLE";
   private _END_ID = "END-STYLE";

   getStyledElementsAsString(selection: Partial<SelectedElement>, extraArguments: Partial<ToolExtraArguments>): string {
      try {
         const { isParameter } = selection;
         const { fontStyle } = extraArguments;
         const currentRange = this.editorService.getRange();
         this._style = fontStyle;
         return this.getFontStyledElementsAsString(currentRange, isParameter);
      } catch (error) {
         this.editorService.logError(error, "Get Font Styled Changes");
         return "";
      }
   }

   insertElementAndSaveRangeSelection(selectedInnerHTML: string, isParameter: boolean): boolean {
      try {
         const currentRange = this.editorService.getRange();
         const wrapperNode = this.editorService.getWrapperElementFromInnerHMTL(selectedInnerHTML);
         /**
          * Inserting a new element in position based on range selection
          */
         this.insertNodeInCurrentRange(currentRange, wrapperNode, isParameter);

         /**
          * saving the new selection range to keep the cursor pointer and selection
          */
         if (isParameter) {
            this.editorService.getNewSelectionRangeForParameter(this._START_ID, this._END_ID);
         } else {
            this.editorService.getNewSelectionRange(this._START_ID, this._END_ID);
         }

         return true;
      } catch (error) {
         this.editorService.logError(error, "Insert Range and Save on Font");
         return false;
      }
   }

   private insertNodeInCurrentRange(currentRange: Range, wrapperNode: Element, isParameter: boolean): void {
      const nodeList = Array.from(wrapperNode.childNodes);
      const { commonAncestorContainer } = currentRange;

      // When the selection IS a TEMPLATE_CONTEXT element
      if (isParameter) {
         const templateElement = this.editorService.getTemplateContextElement();

         if (Boolean(templateElement.previousSibling)) {
            const parentNode = templateElement.parentNode;
            const brotherNodes = this.getAllNextSiblingNodesFromNode(templateElement);
            nodeList.push(...brotherNodes);
            this.removeAllChildFromParent(parentNode, [templateElement, ...brotherNodes]);
            this.appendAllChildren(parentNode, nodeList);
            this.editorService.setTemplateContextElement(<Element>nodeList[0].firstChild);
            return;
         }

         if (this.isSpanChildWithoutSibling(templateElement)) {
            const parentNode = templateElement.parentNode.parentNode;
            const brotherNodes = this.getAllNextSiblingNodesFromNode(templateElement.parentNode);
            nodeList.push(...brotherNodes);
            this.removeAllChildFromParent(parentNode, [templateElement.parentNode, ...brotherNodes]);
            this.appendAllChildren(parentNode, nodeList);
            this.editorService.setTemplateContextElement(<Element>nodeList[0].firstChild);
            return;
         }

         const parentNode = templateElement.parentNode;
         parentNode.removeChild(templateElement);

         while (parentNode.firstChild) {
            nodeList.push(parentNode.firstChild);
            parentNode.removeChild(parentNode.firstChild);
         }

         this.appendAllChildren(parentNode, nodeList);
         this.editorService.setTemplateContextElement(<Element>nodeList[0].firstChild);
         return;
      }

      // When the selection IS a TEXT element
      if (this.isText(commonAncestorContainer)) {
         if (Boolean(commonAncestorContainer.previousSibling)) {
            const parentNode = commonAncestorContainer.parentNode;
            const brotherNodes = this.getAllNextSiblingNodesFromNode(commonAncestorContainer);
            nodeList.push(...brotherNodes);
            this.removeAllChildFromParent(parentNode, [commonAncestorContainer, ...brotherNodes]);
            this.appendAllChildren(parentNode, nodeList);
         } else if (this.isSpanChildWithoutSibling(commonAncestorContainer)) {
            const parentNode = commonAncestorContainer.parentNode.parentNode;
            const brotherNodes = this.getAllNextSiblingNodesFromNode(commonAncestorContainer.parentNode);
            nodeList.push(...brotherNodes);
            this.removeAllChildFromParent(parentNode, [commonAncestorContainer.parentNode, ...brotherNodes]);
            this.appendAllChildren(parentNode, nodeList);
         } else {
            const parentNode = commonAncestorContainer.parentNode;
            parentNode.removeChild(commonAncestorContainer);

            while (parentNode.firstChild) {
               nodeList.push(parentNode.firstChild);
               parentNode.removeChild(parentNode.firstChild);
            }

            this.appendAllChildren(parentNode, nodeList);
         }

         return;
      }

      // When the selection is not a TEXT element and TEMPLATE CONTEXT (parameter)
      (<Element>currentRange.commonAncestorContainer).innerHTML = wrapperNode.innerHTML;
      return;
   }

   private getFontStyledElementsAsString(range: Range, isParameter: boolean): string {
      // to prevent selection issue due to doble click over template-context parameter
      if (isParameter) {
         return this.getTemplateContextStyled(this._style, this.editorService.getTemplateContextElement());
      }

      if (
         this.isText(range.startContainer) &&
         range.startContainer === range.endContainer &&
         range.startContainer === range.commonAncestorContainer
      ) {
         return this.getStartEndStyle(range, this._style);
      }

      const firstNode = range.commonAncestorContainer.firstChild;

      return this.getRecursive(firstNode, range, "NOT-STYLING");
   }

   private getRecursive(currentNode: Node, range: Range, status: StylingStatus): string {
      if (!Boolean(currentNode)) {
         return "";
      }

      if (currentNode === range.startContainer) {
         return (
            this.getStartStyle(currentNode, range.startOffset, this._style) +
            this.getRecursive(currentNode.nextSibling, range, "STYLING")
         );
      }

      if (currentNode === range.endContainer) {
         return (
            this.getEndStyle(currentNode, range.endOffset, this._style) +
            this.getRecursive(currentNode.nextSibling, range, "NOT-STYLING")
         );
      }

      if (currentNode.contains(range.startContainer)) {
         if (this.hasSiblingSameStyle(currentNode, "color")) {
            return "";
         }
         return (
            this.getStartNodeTagAsString(currentNode) +
            this.getRecursive(currentNode.firstChild, range, status) +
            this.getEndNodeTagAsString(currentNode) +
            this.getRecursive(currentNode.nextSibling, range, "STYLING")
         );
      }

      if (currentNode.contains(range.endContainer)) {
         if (this.hasSiblingSameStyle(currentNode, "color")) {
            return "";
         }
         return (
            this.getStartNodeTagAsString(currentNode) +
            this.getRecursive(currentNode.firstChild, range, status) +
            this.getEndNodeTagAsString(currentNode) +
            this.getRecursive(currentNode.nextSibling, range, "NOT-STYLING")
         );
      }

      if (status === "STYLING") {
         if (this.isText(currentNode)) {
            return (
               `<span style="${this._style.property}: ${this._style.value}">${currentNode.nodeValue}</span>` +
               this.getRecursive(currentNode.nextSibling, range, status)
            );
         }

         if (this.isTemplateContext(currentNode)) {
            return (
               `<span style="${this._style.property}: ${this._style.value}">${
                  (<Element>currentNode).outerHTML
               }</span>` + this.getRecursive(currentNode.nextSibling, range, status)
            );
         }

         return (
            this.getStartNodeTagAsString(currentNode) +
            this.getRecursive(currentNode.firstChild, range, status) +
            this.getEndNodeTagAsString(currentNode) +
            this.getRecursive(currentNode.nextSibling, range, status)
         );
      }

      if (this.isText(currentNode)) {
         return currentNode.nodeValue + this.getRecursive(currentNode.nextSibling, range, status);
      }

      return (<Element>currentNode).outerHTML + this.getRecursive(currentNode.nextSibling, range, status);
   }

   private getStartStyle(node: Node, offset: number, style: FontStyleArgument): string {
      if (this.isText(node)) {
         const value = node.nodeValue;
         const part1 = value.slice(0, offset);
         const part2 = value.slice(offset);

         return `${part1}<span id="${this._START_ID}" style="${style.property}: ${style.value}">${part2}</span>`;
      } else {
         return `<span id="${this._START_ID}" style="${style.property}: ${style.value}">${
            (<Element>node).outerHTML
         }</span>`;
      }
   }

   private getEndStyle(node: Node, offset: number, style: FontStyleArgument): string {
      if (this.isText(node)) {
         const value = node.nodeValue;
         const part1 = value.slice(0, offset);
         const part2 = value.slice(offset);

         return `<span id="${this._END_ID}" style="${style.property}: ${style.value}">${part1}</span>${part2}`;
      } else {
         return `<span id="${this._END_ID}" style="${style.property}: ${style.value}">${
            (<Element>node).outerHTML
         }</span>`;
      }
   }

   private getStartEndStyle(range: Range, style: FontStyleArgument): string {
      const value = range.startContainer.nodeValue;
      const part1 = value.slice(0, range.startOffset);
      const part2 = value.slice(range.startOffset, range.endOffset);
      const part3 = value.slice(range.endOffset);

      if (this.isSpanChildWithoutSibling(range.startContainer) && part1 === "" && part3 === "") {
         return this.getStyledSpanWithId(range.startContainer.parentNode, this._START_ID, style);
      }

      if (
         this.hasParentStyles(range.startContainer, ["color", "font-family", "font-size"]) &&
         !this.hasSiblingNode(range.startContainer)
      ) {
         const parentElement = <Element>range.startContainer.parentNode;
         parentElement.innerHTML = `${part1}<span id="${this._START_ID}" style="${style.property}: ${style.value}">${part2}</span>${part3}`;
         return parentElement.outerHTML;
      }

      return `${part1}<span id="${this._START_ID}" style="${style.property}: ${style.value}">${part2}</span>${part3}`;
   }

   private getTemplateContextStyled(style: FontStyleArgument, templateContext: Element): string {
      if (this.isSpanChildWithoutSibling(templateContext)) {
         return this.getStyledSpanWithId(templateContext.parentNode, this._START_ID, style);
      }

      return `<span id="${this._START_ID}" style="${style.property}: ${style.value}">${templateContext.outerHTML}</span>`;
   }

   private getStyledSpanWithId(parentNode: ParentNode, elementId: string, style: FontStyleArgument): string {
      const spanElement = <HTMLElement>parentNode;
      spanElement.id = elementId;

      switch (style.property) {
         case "color":
            spanElement.style.color = style.value;
            break;
         case "font-family":
            spanElement.style.fontFamily = style.value;
            break;
         case "font-size":
            spanElement.style.fontSize = style.value;
            break;
      }

      return spanElement.outerHTML;
   }

   private hasSiblingSameStyle(node: Node, property: StyleProperty) {
      if (!Boolean(node.nextSibling)) {
         return false;
      }

      if (this.isText(node.nextSibling)) {
         return false;
      }

      const siblingElement = <HTMLElement>node.nextSibling;

      return (
         siblingElement.style[0] === "display" &&
         siblingElement.style[1] === property &&
         siblingElement[2] === undefined
      );
   }

   private hasParentStyles(node: Node, property: Array<StyleProperty>): boolean {
      const setProperties = new Set<string>(property);
      const parentNodeAsElement = <HTMLElement>node.parentNode;
      const parentStyleLength = parentNodeAsElement.style.length;
      let hasAnyStyle: boolean = false;

      for (let i = 0; i < parentStyleLength; i++) {
         const parentStyleApplied = parentNodeAsElement.style[i];
         hasAnyStyle = setProperties.has(parentStyleApplied) || hasAnyStyle;
      }

      return hasAnyStyle;
   }
}
